import type {
  AgentProfileRuntime,
  SessionAccessSummary,
  SessionViewerAccess,
  TaskAttachment,
  TaskExecutionStep,
} from '@simple-agent-manager/shared';

import { recordServerTime } from '../server-clock';
import { request } from './client';

// =============================================================================
// Chat Sessions (Project DO)
// =============================================================================

/** Task embed shape — populated in the detail response, added via enrichment for list items. */
export interface ChatSessionTaskEmbed {
  triggeredBy?: string;
  connectorClientName?: string | null;
  id: string;
  /** Canonical, user-safe saved placement diagnostics, including queued runs. */
  placementExplanationJson?: string | null;
  status?: string;
  executionStep?: string | null;
  terminalReason?: string | null;
  errorMessage?: string | null;
  outputBranch?: string | null;
  outputPrUrl?: string | null;
  outputSummary?: string | null;
  finalizedAt?: string | null;
  /** Task execution mode: 'task' (autonomous) or 'conversation' (interactive). */
  taskMode?: 'task' | 'conversation' | null;
  /** Agent profile name hint (human-readable label from dispatch). */
  agentProfileHint?: string | null;
}

/** Session list item, including authoritative task outcome from D1. */
export interface ChatSessionListItem {
  task?: ChatSessionTaskEmbed;
  id: string;
  workspaceId: string | null;
  taskId: string | null;
  createdByUserId?: string | null;
  createdBy?: {
    id: string;
    name: string | null;
    email: string | null;
    image: string | null;
    avatarUrl: string | null;
  } | null;
  isMine?: boolean;
  topic: string | null;
  status: string;
  messageCount: number;
  startedAt: number;
  endedAt: number | null;
  createdAt: number;
  /** Timestamp when the agent completed its work (null if still running or never started). */
  agentCompletedAt?: number | null;
  /** Timestamp of the last message in the session. */
  lastMessageAt?: number | null;
  /** Whether the session is idle (agent completed but session still active). */
  isIdle?: boolean;
  /** Whether the session has been stopped. */
  isTerminated?: boolean;
  /** Full workspace URL for direct access. */
  workspaceUrl?: string | null;
  /** Scheduled cleanup timestamp for idle sessions (from idle_cleanup_schedule). */
  cleanupAt?: number | null;
  /** Active agent session ID (ULID) from D1, used for ACP WebSocket routing. */
  agentSessionId?: string | null;
  /** Agent type from ACP session (e.g., 'claude-code', 'openai-codex'). */
  agentType?: string | null;
  /** Durable attention marker summary from backend (null = no active marker). */
  attention?: {
    markerId: string;
    kind: string;
    createdAt: number;
    expiresAt: number | null;
    reason: string | null;
    options: string[];
  } | null;
}

/**
 * Enriched session — extends the list item with an optional task embed.
 *
 * This is the type used by components that need to distinguish completed/failed
 * tasks from stopped sessions. The `task` field is populated either by:
 * - The detail API (`GET /api/projects/:id/sessions/:sessionId`)
 * - List API outcome, plus frontend hints from `taskInfoMap` (see SessionTreeItem)
 */
export interface ChatSessionResponse extends ChatSessionListItem {
  task?: ChatSessionTaskEmbed;
  /**
   * What the viewer may do in this session (session detail only). `owner` and `grantee`
   * drive the agent; `reader` can read and comment. Absent on list items and on detail
   * responses cached before shared sessions shipped — see `resolveSessionViewerAccess`.
   */
  viewerAccess?: SessionViewerAccess;
  /** The owner and active shared-access grants (session detail only). */
  access?: SessionAccessSummary;
}

export interface ChatSessionListResponse {
  sessions: ChatSessionListItem[];
  total: number;
}

export interface ChatMessageResponse {
  id: string;
  sessionId: string;
  role: string;
  content: string;
  toolMetadata: Record<string, unknown> | null;
  createdAt: number;
  sequence?: number | null;
  /** "system" for SAM-injected messages the UI collapses; absent for normal messages. */
  origin?: 'user' | 'system' | null;
}

/** Persisted session state snapshot from the DO (for catch-up on page load). */
export interface SessionStateSnapshot {
  activity: 'idle' | 'prompting' | 'recovering' | 'error' | 'stopped';
  activityAt: number;
  statusError: string | null;
  currentPlan: Array<{ content: string; status: string }> | null;
  planUpdatedAt: number | null;
  promptStartedAt: number | null;
  agentType: string | null;
  lastStopReason: string | null;
  runtimeWorkState: 'inactive' | 'active' | 'settling' | null;
  runtimeWorkCount: number | null;
  runtimeWorkSource: string | null;
  runtimeWorkUpdatedAt: number | null;
  runtimeWorkProgressAt: number | null;
  recoveryStatus?: 'waking' | 'restored' | 'failed' | null;
  /**
   * Execution step of the replacement TaskRunner waking this session. Drives
   * phase-level wake progress; null when no wake is in flight.
   */
  wakePhase?: TaskExecutionStep | null;
}

export interface ChatSessionDetailResponse {
  session: ChatSessionResponse;
  messages: ChatMessageResponse[];
  hasMore: boolean;
  state?: SessionStateSnapshot | null;
}

export interface ChatSessionStateResponse {
  state: SessionStateSnapshot | null;
  agentSessionId: string | null;
  agentType: string | null;
}

export interface ChatMessagesListResponse {
  messages: ChatMessageResponse[];
  hasMore: boolean;
}

export async function listChatSessions(
  projectId: string,
  params: { status?: string; limit?: number; offset?: number; scope?: 'my' | 'all' } = {}
): Promise<ChatSessionListResponse> {
  const searchParams = new URLSearchParams();
  if (params.status) searchParams.set('status', params.status);
  if (params.limit !== undefined) searchParams.set('limit', String(params.limit));
  if (params.offset !== undefined) searchParams.set('offset', String(params.offset));
  if (params.scope) searchParams.set('scope', params.scope);

  const qs = searchParams.toString();
  const endpoint = qs
    ? `/api/projects/${projectId}/sessions?${qs}`
    : `/api/projects/${projectId}/sessions`;

  return request<ChatSessionListResponse>(endpoint);
}

// =============================================================================
// Cross-Project Chat Sessions (D1 session_summaries)
// =============================================================================

export interface SessionSummaryItem {
  id: string;
  projectId: string;
  projectName: string;
  userId: string;
  status: string;
  topic: string | null;
  taskId: string | null;
  workspaceId: string | null;
  messageCount: number;
  startedAt: number;
  lastMessageAt: number | null;
  agentCompletedAt: number | null;
  endedAt: number | null;
  updatedAt: number;
}

export interface RecentChatsApiResponse {
  sessions: SessionSummaryItem[];
  totalActive: number;
}

export interface AllChatsApiResponse {
  sessions: SessionSummaryItem[];
  total: number;
}

/** Fetch recent active sessions across all projects (single D1 query). */
export async function getRecentChats(
  params: { limit?: number; staleThreshold?: number } = {}
): Promise<RecentChatsApiResponse> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) searchParams.set('limit', String(params.limit));
  if (params.staleThreshold !== undefined)
    searchParams.set('staleThreshold', String(params.staleThreshold));

  const qs = searchParams.toString();
  return request<RecentChatsApiResponse>(qs ? `/api/chats/recent?${qs}` : '/api/chats/recent');
}

/** Fetch all sessions across all projects with pagination (single D1 query). */
export async function getAllChats(
  params: { limit?: number; offset?: number; status?: string } = {}
): Promise<AllChatsApiResponse> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) searchParams.set('limit', String(params.limit));
  if (params.offset !== undefined) searchParams.set('offset', String(params.offset));
  if (params.status) searchParams.set('status', params.status);

  const qs = searchParams.toString();
  return request<AllChatsApiResponse>(qs ? `/api/chats?${qs}` : '/api/chats');
}

// =============================================================================
// Per-Project Chat Session Detail
// =============================================================================

/** `before`/`after` are exact cursors from `lib/message-paging` (the rows at a page's edge). */
export async function getChatSession(
  projectId: string,
  sessionId: string,
  params: { limit?: number; before?: string; after?: string; signal?: AbortSignal } = {}
): Promise<ChatSessionDetailResponse> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) searchParams.set('limit', String(params.limit));
  if (params.before !== undefined) searchParams.set('before', params.before);
  if (params.after !== undefined) searchParams.set('after', params.after);

  const qs = searchParams.toString();
  const endpoint = qs
    ? `/api/projects/${projectId}/sessions/${sessionId}?${qs}`
    : `/api/projects/${projectId}/sessions/${sessionId}`;

  const detail = await request<ChatSessionDetailResponse>(
    endpoint,
    params.signal ? { signal: params.signal } : {}
  );
  recordServerTime(detail.session?.access?.serverTime);
  return detail;
}

export async function getChatSessionState(
  projectId: string,
  sessionId: string,
  params: { signal?: AbortSignal } = {}
): Promise<ChatSessionStateResponse> {
  return request<ChatSessionStateResponse>(
    `/api/projects/${projectId}/sessions/${sessionId}/state`,
    params.signal ? { signal: params.signal } : {}
  );
}

export async function listChatMessages(
  projectId: string,
  sessionId: string,
  params: {
    limit?: number;
    before?: string;
    after?: string;
    roles?: string[];
    compact?: boolean;
    order?: 'asc' | 'desc';
    signal?: AbortSignal;
  } = {}
): Promise<ChatMessagesListResponse> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) searchParams.set('limit', String(params.limit));
  if (params.before !== undefined) searchParams.set('before', params.before);
  if (params.after !== undefined) searchParams.set('after', params.after);
  if (params.roles && params.roles.length > 0) searchParams.set('roles', params.roles.join(','));
  if (params.compact !== undefined) searchParams.set('compact', String(params.compact));
  if (params.order !== undefined) searchParams.set('order', params.order);

  const qs = searchParams.toString();
  const endpoint = qs
    ? `/api/projects/${projectId}/sessions/${sessionId}/messages?${qs}`
    : `/api/projects/${projectId}/sessions/${sessionId}/messages`;

  return request<ChatMessagesListResponse>(
    endpoint,
    params.signal ? { signal: params.signal } : {}
  );
}

/**
 * Lazy-load the tool_metadata.content array for a single message.
 * Used by compact mode when the user expands a tool call card.
 */
export async function getMessageToolContent(
  projectId: string,
  sessionId: string,
  messageId: string
): Promise<{
  content: unknown[];
  source?: 'inline' | 'archive' | 'archived_unavailable';
  archived?: { archivedAt: number; contentBytes: number; reason?: string };
}> {
  return request<{
    content: unknown[];
    source?: 'inline' | 'archive' | 'archived_unavailable';
    archived?: { archivedAt: number; contentBytes: number; reason?: string };
  }>(`/api/projects/${projectId}/sessions/${sessionId}/messages/${messageId}/tool-content`);
}

export async function createChatSession(
  projectId: string,
  data: { workspaceId?: string; topic?: string } = {}
): Promise<{ id: string }> {
  return request<{ id: string }>(`/api/projects/${projectId}/sessions`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export interface StartInstantChatSessionRequest {
  message: string;
  agentProfileId?: string;
  skillId?: string;
  parentTaskId?: string;
  /** An open idea in this project this conversation starts from. The server links it
   * (`discusses`) before the agent receives its first prompt. */
  sourceIdeaId?: string;
}

export interface StartInstantChatSessionResponse {
  status: 'starting' | 'running';
  taskId: string;
  runtime: {
    runtime: AgentProfileRuntime;
    reason: string;
  };
  sessionId: string;
  workspaceId: string;
  nodeId: string;
  agentSessionId?: string;
  acpSessionId?: string;
  workspaceUrl: string;
  timings?: {
    totalDurationMs: number;
    preContainerDurationMs: number;
    containerLaunchDurationMs: number;
    /** @deprecated backward-compat alias for totalDurationMs; prefer totalDurationMs. */
    setupDurationMs: number;
    /** @deprecated backward-compat alias for containerLaunchDurationMs; prefer containerLaunchDurationMs. */
    installDurationMs: number;
    agentReadyDurationMs: number;
    workspaceCreateDurationMs: number;
    acpSessionCreateDurationMs: number;
    acpSessionStartDurationMs: number;
  };
}

export async function startInstantChatSession(
  projectId: string,
  data: StartInstantChatSessionRequest
): Promise<StartInstantChatSessionResponse> {
  return request<StartInstantChatSessionResponse>(`/api/projects/${projectId}/sessions/start`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function stopChatSession(
  projectId: string,
  sessionId: string
): Promise<{ status: string; workspaceDeleted?: boolean }> {
  return request<{ status: string; workspaceDeleted?: boolean }>(
    `/api/projects/${projectId}/sessions/${sessionId}/stop`,
    {
      method: 'POST',
    }
  );
}

export async function resetIdleTimer(
  projectId: string,
  sessionId: string
): Promise<{ cleanupAt: number }> {
  return request<{ cleanupAt: number }>(
    `/api/projects/${projectId}/sessions/${sessionId}/idle-reset`,
    {
      method: 'POST',
    }
  );
}

/** Send a follow-up prompt to the running agent via the REST API. */
export async function sendFollowUpPrompt(
  projectId: string,
  sessionId: string,
  content: string,
  attachments?: TaskAttachment[]
): Promise<{ status: string; sessionId?: string; message?: string }> {
  return request<{ status: string; sessionId?: string; message?: string }>(
    `/api/projects/${projectId}/sessions/${sessionId}/prompt`,
    {
      method: 'POST',
      body: JSON.stringify({ content, attachments }),
    }
  );
}

export async function resolveAttentionAnswer(
  projectId: string,
  sessionId: string,
  markerId: string,
  answer: string
): Promise<{
  resolved: boolean;
  alreadyResolved: boolean;
  inFlight?: boolean;
  answer: string;
}> {
  return request<{
    resolved: boolean;
    alreadyResolved: boolean;
    inFlight?: boolean;
    answer: string;
  }>(`/api/projects/${projectId}/sessions/${sessionId}/attention/${markerId}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ answer }),
  });
}

/** Cancel the current in-flight prompt on the running agent session. */
export async function cancelAgentPrompt(
  projectId: string,
  sessionId: string
): Promise<{ status: string; message: string }> {
  return request<{ status: string; message: string }>(
    `/api/projects/${projectId}/sessions/${sessionId}/cancel`,
    { method: 'POST' }
  );
}

// =============================================================================
// Activity Events
// =============================================================================

export interface ActivityEventResponse {
  id: string;
  eventType: string;
  actorType: string;
  actorId: string | null;
  workspaceId: string | null;
  sessionId: string | null;
  taskId: string | null;
  payload: Record<string, unknown> | null;
  createdAt: number;
}

export interface ActivityEventsListResponse {
  events: ActivityEventResponse[];
  hasMore: boolean;
}

export async function listActivityEvents(
  projectId: string,
  params?: { eventType?: string; sessionId?: string; before?: number; limit?: number }
): Promise<ActivityEventsListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.eventType) searchParams.set('eventType', params.eventType);
  if (params?.sessionId) searchParams.set('sessionId', params.sessionId);
  if (params?.before) searchParams.set('before', String(params.before));
  if (params?.limit) searchParams.set('limit', String(params.limit));

  const qs = searchParams.toString();
  const endpoint = `/api/projects/${projectId}/activity${qs ? `?${qs}` : ''}`;
  return request<ActivityEventsListResponse>(endpoint);
}
